// FinTrack — Pure utility functions
// Zero state access (except where noted). All modules import from here.

/**
 * Escape HTML special characters to prevent XSS
 * @param {*} s - Value to escape
 * @returns {string} HTML-safe string
 */
export function esc(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Format number in Serbian locale (e.g., 1.234,56)
 * @param {number} n - Number to format
 * @returns {string} Formatted string with Serbian thousands/decimal separators
 */
export function fNum(n) {
  return new Intl.NumberFormat('sr-RS', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  }).format(n);
}

// Iznos sa tačno 2 decimale (za PDF izveštaje — tačan iznos kao u Excelu, npr. 14.166.066,85)
export function fNum2(n) {
  return new Intl.NumberFormat('sr-RS', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
}

/**
 * Format number as raw decimal for clipboard copy (e.g., 1234.56)
 * @param {number} n - Number to format
 * @returns {string} Fixed 2-decimal string
 */
export function rawNum(n) {
  return n.toFixed(2);
}

/**
 * Parse number from Serbian format (dots as thousands, comma as decimal)
 * @param {string|number|null} v - Value to parse
 * @returns {number} Parsed number, or 0 if invalid/empty
 */
export function pNum(v) {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v;
  return parseFloat(String(v).replace(/\s/g, '').replace(/\./g, '').replace(',', '.')) || 0;
}

/**
 * Parse date from various formats (dd.mm.yyyy, dd/mm/yyyy, yyyy-mm-dd, etc.)
 * @param {string|Date|null} v - Value to parse
 * @returns {Date|null} Parsed Date object, or null if invalid/empty
 */
export function pDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  // Skini eventualnu trailing tačku iz srpskog formata "01.06.2026."
  const s = String(v).trim().replace(/\.$/, '');
  let m = s.match(/^(\d{1,2})[.\/](\d{1,2})[.\/](\d{4})$/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  // 2-cifrena godina "01.06.26" -> 2026 (inače new Date() zameni dan/mesec ili vrati Invalid)
  m = s.match(/^(\d{1,2})[.\/](\d{1,2})[.\/](\d{2})$/);
  if (m) return new Date(2000 + +m[3], +m[2] - 1, +m[1]);
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/**
 * Copy a value to the system clipboard
 * @param {string} v - Text to copy
 * @returns {void}
 */
export function cpVal(v) {
  navigator.clipboard.writeText(v).then(() => {
    // toast is not available here; callers should handle notification
  }).catch(e => console.error('SBIS:', e));
}

/**
 * Create a debounced version of a function
 * @param {Function} fn - Function to debounce
 * @param {number} ms - Delay in milliseconds
 * @returns {Function} Debounced function that delays invocation until ms after last call
 */
export function debounce(fn, ms) {
  let t;
  return function () {
    clearTimeout(t);
    t = setTimeout(fn, ms);
  };
}

/**
 * Paginate an array — returns a slice plus metadata
 * @param {Array} arr - Full array to paginate
 * @param {number} page - Zero-based page index
 * @param {number} pageSize - Items per page
 * @returns {{ items: Array, total: number, pages: number, page: number }} Paginated result
 */
export function paginate(arr, page, pageSize) {
  return {
    items: arr.slice(page * pageSize, (page + 1) * pageSize),
    total: arr.length,
    pages: Math.ceil(arr.length / pageSize),
    page
  };
}

/**
 * Render pagination controls (prev/next buttons + page indicator) into a DOM container
 * @param {string} containerId - DOM element ID for the pagination container
 * @param {number} current - Zero-based current page index
 * @param {number} totalPages - Total number of pages
 * @param {function(number): void} onPageFn - Callback invoked with the new page index
 * @returns {HTMLDivElement} The pagination container element
 */
export function renderPagination(containerId, current, totalPages, onPageFn) {
  let el = document.getElementById(containerId);
  if (!el) {
    el = document.createElement('div');
    el.id = containerId;
    el.style.cssText =
      'display:flex;justify-content:center;align-items:center;gap:8px;padding:12px;font-size:13px;color:var(--tx2)';
  }
  el.textContent = '';
  if (totalPages <= 1) return el;
  const bp = document.createElement('button');
  bp.className = 'bsm';
  bp.textContent = '\u25C0';
  bp.disabled = current === 0;
  bp.onclick = () => onPageFn(current - 1);
  const info = document.createElement('span');
  info.textContent = (current + 1) + ' / ' + totalPages;
  const bn = document.createElement('button');
  bn.className = 'bsm';
  bn.textContent = '\u25B6';
  bn.disabled = current >= totalPages - 1;
  bn.onclick = () => onPageFn(current + 1);
  el.appendChild(bp);
  el.appendChild(info);
  el.appendChild(bn);
  return el;
}

/**
 * Format a Date as YYYY-MM-DD using LOCAL date parts.
 * Use instead of toISOString().slice(0,10) for user-selected calendar dates:
 * toISOString() converts local-midnight to UTC and rolls the date back a day in UTC+ zones.
 * @param {Date} d
 * @returns {string} local YYYY-MM-DD
 */
export function isoLocal(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/**
 * Wrap a value in double-quotes for CSV export, escaping inner quotes
 * @param {*} v - Value to quote
 * @returns {string} CSV-safe quoted string
 */
export function csvQ(v) {
  const s = String(v == null ? '' : v);
  return '"' + s.replace(/"/g, '""') + '"';
}

/**
 * CSV-safe quoting for TEXT fields that may be attacker-influenced (komitent/category names):
 * additionally neutralizes leading formula triggers (= + - @ tab CR) to prevent CSV/formula
 * injection when opened in Excel/LibreOffice. Do NOT use for numeric cells (would prefix negatives).
 * @param {*} v
 * @returns {string}
 */
export function csvT(v) {
  const s = String(v == null ? '' : v);
  const g = /^[=+\-@\t\r]/.test(s) ? ("'" + s) : s;
  return '"' + g.replace(/"/g, '""') + '"';
}

/**
 * Validate that an Excel header row contains all required column names
 * @param {Array<string>} hdr - Header row values from the spreadsheet
 * @param {Array<Array<string>>} requiredNames - Array of name groups; each group is an array of acceptable column name variants
 * @returns {Array<string>} List of missing column names (first variant from each missing group)
 */
export function validateExcelColumns(hdr, requiredNames) {
  const missing = [];
  for (const names of requiredNames) {
    const found = names.some(
      n => hdr.findIndex(h => h && String(h).trim().toLowerCase().includes(n.toLowerCase())) >= 0
    );
    if (!found) missing.push(names[0]);
  }
  return missing;
}
